/**
 * 1.7.8 — the day bends (docs/DECISIONS.md D-1.7.8-0…3). Elijah, 8 Oct 2026, after a real day: "A tenant needed to
 * move to 3:30, and the crew cleaned the office at 2:30 because they had nothing to do. I could not book the office
 * for the time they actually started … and I could not move the tenant. I can't be stuck."
 *
 * Times are still labels. SharePoint keeps day, period and position, and the processors read nothing new. On the day
 * itself a visit may carry a **chosen start** — minutes after midnight on the service clock — kept by the app in
 * `_app/day-starts/<yyyyMMdd>.json` (a booking for today made with a time keeps it in its own request's form). The
 * day plan (shared/dayplan.ts planDay) pins that one visit to its chosen start; every other visit keeps the time the
 * plan and the running day give it.
 *
 * This file is the rules in one place, with no dependencies, so the app and the API share them:
 *   windowsOf      each visit's window on the day (start → start + length; a started visit from its arrival stamp)
 *   overlapOf      does a visit put at a time run into the next one in the same crew's lane, and by how much
 *   pushPlan       "Push 1104 to 3:50" — the next one and anything it would then run into, each by the same amount
 *   pastEndOf      what would end after the crew's end of day
 *   overlapsOnDay  the overlaps the office kept, for the cards' words
 *   freeGap        "Free 2:30–3:30" — the time given back before the next visit
 *   whenChips      the 30-minute chips of the When control
 */
import { dayPlanFor, formatClock, type DayPlan, type DayPlanSettings, type PlannedPosition } from './dayplan';

export const DAY_STARTS_FOLDER = '_app/day-starts';

/** A chosen start, as the app keeps it for one visit on one day. */
export interface DayStart {
  /** Minutes after midnight, America/New_York. */
  start: number;
  /** Who chose it, and when (an ISO instant): "recorded 2:50 by Elijah" when the start is earlier than this. */
  by: string;
  at: string;
  /** The one optional line: why. */
  why?: string;
  /** What the card showed before (a move, a push). */
  was?: number | null;
  /** A length chosen with the move ("sometimes an hour"). */
  minutes?: number | null;
  /** A push: the apartment whose booking or move pushed this visit. */
  pushedBy?: string | null;
  /** Booked for today at this time (its position keeps a free position's place on the plan). */
  booked?: boolean;
}
export type DayStarts = Record<string, DayStart>;

export type BendLane = 'A' | 'B' | null;
export type BendState = 'planned' | 'started' | 'finished';

/** What the rules need to know of a visit beside the plan. */
export interface BendVisit {
  id: string;
  unit: string;
  position: number;
  /** A tenant's visit (a staff unit or an office has nobody to tell). */
  tenant: boolean;
  state: BendState;
}

export interface BendWindow extends BendVisit {
  lane: BendLane;
  start: number;
  end: number;
  minutes: number;
  /** The start is one the office chose. */
  chosen: boolean;
}

/** Each visit's window on the laid-out day: a started one from its stamp, the others where the day now has them. */
export function windowsOf(plan: readonly PlannedPosition[], visits: readonly BendVisit[]): BendWindow[] {
  const out: BendWindow[] = [];
  for (const v of visits) {
    const p = plan.find((x) => x.position === v.position);
    if (!p || !p.booked) continue;
    const start = p.expectedStart ?? p.startMinutes;
    const end = p.expectedStart !== null && p.expectedEnd !== null ? p.expectedEnd : p.expectedStart !== null ? p.expectedStart + p.minutes : p.endMinutes;
    out.push({ ...v, lane: p.lane ?? null, start, end, minutes: p.minutes, chosen: !!p.chosen });
  }
  return out.sort((a, b) => a.start - b.start || a.position - b.position);
}

/** A visit about to be put at a time: a booking (no id yet), a move, a longer length. */
export interface Candidate {
  id: string | null;
  unit: string;
  lane: BendLane;
  start: number;
  minutes: number;
}

export interface Overlap {
  id: string;
  unit: string;
  /** When the visit it runs into starts. */
  at: number;
  /** By how many minutes. */
  by: number;
  /** False when that visit has started: the crew is inside, it cannot be pushed. */
  canPush: boolean;
  /** 15 Oct 2026: the visit is already there at the chosen time (it started earlier and runs past it) — not the next one. */
  occupant?: boolean;
  /** The crew is inside it. */
  started?: boolean;
}

const sameLane = (w: BendWindow, c: Candidate) => (w.lane ?? null) === (c.lane ?? null);
const ahead = (ws: readonly BendWindow[], c: Candidate) => ws.filter((w) => w.id !== c.id && sameLane(w, c) && w.state !== 'finished' && w.start >= c.start);

/** "This runs into 1104 at 3:30 by 20 minutes" — the next visit in the same lane, or null when there is room. */
export function overlapOf(ws: readonly BendWindow[], c: Candidate): Overlap | null {
  const end = c.start + c.minutes;
  // 15 Oct 2026: someone already there at that time (began earlier, still running at it) is the first to ask about
  const there = ws.find((w) => w.id !== c.id && sameLane(w, c) && w.state !== 'finished' && w.start < c.start && w.end > c.start);
  if (there) return { id: there.id, unit: there.unit, at: there.start, by: there.end - c.start, canPush: false, occupant: true, started: there.state === 'started' };
  const next = ahead(ws, c)[0];
  if (!next || next.start >= end) return null;
  return { id: next.id, unit: next.unit, at: next.start, by: end - next.start, canPush: next.state === 'planned', started: next.state === 'started' };
}

export interface PushMove {
  id: string;
  unit: string;
  tenant: boolean;
  from: number;
  to: number;
  /** When it would end at its new time. */
  end: number;
}

/**
 * "Push 1104 to 3:50 (and anything after it, each by the same amount, in order)": the next visit moves by the
 * overlap, and each visit after it that the one before would then run into moves by the same amount. A visit with
 * room before it stays where it is; a visit the crew has started is never moved, and the chain stops at it.
 * Null when there is nothing to push or the next visit cannot move.
 */
export function pushPlan(ws: readonly BendWindow[], c: Candidate): { by: number; moves: PushMove[] } | null {
  const o = overlapOf(ws, c);
  if (!o || !o.canPush) return null;
  const moves: PushMove[] = [];
  let reach = c.start + c.minutes;
  for (const w of ahead(ws, c)) {
    if (w.start >= reach || w.state !== 'planned') break;
    moves.push({ id: w.id, unit: w.unit, tenant: w.tenant, from: w.start, to: w.start + o.by, end: w.end + o.by });
    reach = w.end + o.by;
  }
  return { by: o.by, moves };
}

export interface PastEnd {
  /** Null: the visit being booked. */
  id: string | null;
  unit: string;
  end: number;
}

/** What would end after the crew's end of day: the visit itself and anything a push moves. */
export function pastEndOf(c: Candidate, moves: readonly PushMove[], dayEnd: number): PastEnd[] {
  const out: PastEnd[] = [];
  if (c.start + c.minutes > dayEnd) out.push({ id: c.id, unit: c.unit, end: c.start + c.minutes });
  for (const m of moves) if (m.end > dayEnd) out.push({ id: m.id, unit: m.unit, end: m.end });
  return out;
}

/**
 * The overlaps that stand on the day, for the cards ("overlaps 1104 by 20 min" on both). Only where one of the two
 * has a chosen start: an overlap is something the office decided to keep, never a guess about a late crew.
 */
export function overlapsOnDay(ws: readonly BendWindow[]): Map<string, { id: string; unit: string; by: number }[]> {
  const out = new Map<string, { id: string; unit: string; by: number }[]>();
  const add = (a: BendWindow, b: BendWindow, by: number) => out.set(a.id, [...(out.get(a.id) ?? []), { id: b.id, unit: b.unit, by }]);
  const open = ws.filter((w) => w.state !== 'finished');
  for (let i = 0; i < open.length; i++) {
    for (let j = i + 1; j < open.length; j++) {
      const a = open[i]!;
      const b = open[j]!;
      if ((a.lane ?? null) !== (b.lane ?? null) || (!a.chosen && !b.chosen)) continue;
      const by = Math.min(a.end, b.end) - Math.max(a.start, b.start);
      if (by <= 0) continue;
      add(a, b, by);
      add(b, a, by);
    }
  }
  return out;
}

export interface FreeGap {
  lane: BendLane;
  from: number;
  to: number;
}

const ceil5 = (m: number) => Math.ceil(m / 5) * 5;
export const MIN_FREE_GAP = 30;

/**
 * "Free 2:30–3:30 · Book something here" — the time given back. Once the crew has started (a stamp in the lane),
 * the time from when they are free (the last started visit's end plus the gap between visits, or now) to the next
 * visit still to come, when it is half an hour or more. Lunch is not free time. Nothing moves by itself: the card
 * is an offer.
 */
export function freeGaps(ws: readonly BendWindow[], o: { nowMinutes: number; gapMinutes: number; plan: DayPlan }): FreeGap[] {
  const out: FreeGap[] = [];
  const lanes = [...new Set(ws.map((w) => w.lane ?? null))];
  for (const lane of lanes) {
    const mine = ws.filter((w) => (w.lane ?? null) === lane);
    const begun = mine.filter((w) => w.state !== 'planned' && w.start <= o.nowMinutes);
    if (!begun.length) continue;
    const working = mine.some((w) => w.state === 'started');
    const freeAt = Math.max(...begun.map((w) => w.end)) + o.gapMinutes;
    // a visit under way that is running past its length: the crew is inside, nothing is free yet
    if (working && freeAt < o.nowMinutes) continue;
    let from = ceil5(Math.max(freeAt, o.nowMinutes));
    const next = mine.filter((w) => w.state === 'planned' && w.start >= from).sort((a, b) => a.start - b.start)[0];
    if (!next) continue;
    let to = next.start;
    // a visit still planned before `from` (the crew is behind) means there is no free time to offer
    if (mine.some((w) => w.state === 'planned' && w.start < from && w.end > from)) continue;
    const lunchEnd = o.plan.lunchStartMinutes + o.plan.lunchMinutes;
    if (o.plan.lunchMinutes > 0 && from < lunchEnd && to > o.plan.lunchStartMinutes) {
      // lunch is inside: offer the longer side of it
      const before = o.plan.lunchStartMinutes - from;
      const after = to - lunchEnd;
      if (before >= after) to = o.plan.lunchStartMinutes;
      else from = lunchEnd;
    }
    if (to - from >= MIN_FREE_GAP) out.push({ lane, from, to });
  }
  return out.sort((a, b) => a.from - b.from);
}

/** The When control's chip row: every half hour from 9:00 (or the day's start, when earlier) to half an hour before the day's end. */
export function whenChips(dayKey: string, settings: DayPlanSettings = {}): number[] {
  const p = dayPlanFor(dayKey, settings);
  const first = Math.min(9 * 60, Math.floor(p.startMinutes / 30) * 30);
  const out: number[] = [];
  for (let m = first; m <= p.endMinutes - 30; m += 30) out.push(m);
  return out;
}

/** A start the app takes: a whole minute of the day. */
export function validStart(v: unknown): number | null {
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isInteger(n) && n >= 0 && n < 24 * 60 ? n : null;
}

/** The chosen start a booking's own form carries (a tenant booked for today at a time), as a DayStart. */
export function startOfForm(form: Record<string, unknown> | undefined): DayStart | null {
  const c = form?.startChosen as Partial<DayStart> | undefined;
  const start = c && typeof c === 'object' ? validStart(c.start) : null;
  if (start === null || typeof c!.by !== 'string' || typeof c!.at !== 'string') return null;
  return { start, by: c!.by, at: c!.at, booked: true };
}

/** "3:30–5:00" */
export const windowWords = (w: { start: number; end: number }) => `${formatClock(w.start)}–${formatClock(w.end)}`;

/** The sentence the sheet says before saving (English: the API's refusal; the screens say it from the numbers). */
export function overlapWords(o: Overlap): string {
  return `This runs into ${o.unit} at ${formatClock(o.at)} by ${o.by} minutes.`;
}

/* ---------------- the API's shapes ---------------- */

/** POST /api/housekeeping/day-start — Move within the day (and "How long", when the length changes with it). */
export interface DayStartRequest {
  appointmentId: string;
  start: number;
  minutes?: number;
  why?: string;
  /** The answer to the overlap: absent on the first press — the server answers 409 `overlap` with the choices.
   *  15 Oct 2026: `swap` — the visit already at that time takes this visit's old time (the office's usual answer). */
  onOverlap?: OverlapAnswerKind;
  /**
   * 17 Oct 2026: the Reschedule sheet's "Email the tenant their new time", on a move to another time on the visit's own
   * day — the same switch a move to another day carries (`emailNewTime`). true: this press queues the moved tenant's
   * email (into the Outbox, through the cancel window); false: nobody is emailed. Either way the switch has decided for
   * the moved tenant, so they are not offered again under Who needs to know. Absent (Move on a card, which has no
   * switch): as before — nobody is emailed and the moved tenant is offered under Who needs to know. A push never emails.
   */
  emailNewTime?: boolean;
}

export type OverlapAnswerKind = 'push' | 'keep' | 'swap';

/** The 409 a booking or a move answers when it would run into the next visit and nobody has chosen yet. */
export interface OverlapAnswer {
  error: 'overlap';
  message: string;
  overlap: Overlap;
  /** "Push": every card that would move, with its new time (null: the next visit has started and cannot move). */
  push: { by: number; moves: PushMove[] } | null;
  /** 15 Oct 2026: "Swap": the visit at that time takes this visit's old time (a move only; never one the crew is inside). */
  swap?: { unit: string; to: number } | null;
  /** What would end after the day's end — after the push, and if both are kept. */
  pastEndIfPushed: PastEnd[];
  pastEndIfKept: PastEnd[];
  dayEnd: number;
}

/** A pushed tenant, for Who needs to know (1.5.22): the tick "Email the tenant their new time" is off by default. */
export interface ToldRow {
  appointmentId: string;
  unit: string;
  name: string;
  from: number;
  to: number;
  /** False: no email the app may use — tell them by phone. */
  canEmail: boolean;
}

/** What a booking for today at a chosen time answers beside the booking itself. */
export interface BookedStart {
  moved: PushMove[];
  pastEnd: PastEnd[];
  kept: Overlap | null;
  toTell: ToldRow[];
}

export interface DayStartResponse {
  appointmentId: string;
  unchanged?: boolean;
  before: number;
  start: number;
  minutes: number;
  moved: PushMove[];
  pastEnd: PastEnd[];
  /** The overlap that was kept ("Keep both as they are"), for the announcement. */
  kept: Overlap | null;
  toTell: ToldRow[];
  /** 19 Oct 2026 (Eli): the visit now ends after the crew's end of day — a warning, never a refusal; the move is saved. */
  endsAfterDayEnd?: { end: number; dayEnd: number };
  /** 17 Oct 2026: the moved tenant's email this press queued (`emailNewTime` on, and the tenant has an email the app may use). */
  communicationId?: string | null;
  /** 17 Oct 2026: the switch was on but the email could not be written (the wording) — the move itself is saved; said as written. */
  emailNotQueued?: string;
}

/**
 * 14 Oct 2026 (Eli): a time that has already passed today is not a booking — it is the office putting the record in
 * order ("the crew saw them at 9:30"). It needs a written reason, and the visit is then closed out as usual (photos
 * optional). A quarter of an hour of grace covers a crew that started a little before the office pressed.
 */
export const PAST_GRACE_MINUTES = 15;
export function isPastStart(start: number, nowMinutes: number): boolean {
  return start < nowMinutes - PAST_GRACE_MINUTES;
}
export const PAST_WHY_WORDS =
  'That time has already passed today. To put a visit the crew already did on the record, write why in the reason box (for example: “the crew saw them at 9:30”).';
